import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import PermissionGuard, { EditButton, DeleteButton } from '../../components/permissions/PermissionGuard'
import apiService from '../../services/api'
import {
  HiOutlineUserGroup,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowDownTray,
  HiOutlineArrowUpTray,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineGlobeAmericas,
  HiOutlineXMark,
  HiOutlineFunnel,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import ClientImportModal from '../../components/clients/ClientImportModal'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

export default function ClientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canCreate } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [clients, setClients] = useState([])
  const [pageLoading, setPageLoading] = useState(true)
  const [clientsLoading, setClientsLoading] = useState(false)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [isSearching, setIsSearching] = useState(false)
  const [selectedClients, setSelectedClients] = useState([])
  const [showImportModal, setShowImportModal] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const [filters, setFilters] = useState({
    name:    searchParams.get('name')    || '',
    email:   searchParams.get('email')   || '',
    mobile:  searchParams.get('mobile')  || '',
    country: searchParams.get('country') || '',
  })
  const [sortBy, setSortBy] = useState(searchParams.get('ordering') || '-created_at')
  const [showFilters, setShowFilters] = useState(false)

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('page_size')) || 20)

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput)
        setCurrentPage(1)
      }
      setIsSearching(false)
    }, 500)
    if (searchInput !== searchQuery && searchInput.length > 0) setIsSearching(true)
    return () => clearTimeout(id)
  }, [searchInput, searchQuery])

  useEffect(() => {
    const isFirstLoad = pageLoading && clients.length === 0
    fetchClients(isFirstLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, filters, sortBy, itemsPerPage, ownerAccountId])

  useEffect(() => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('search', searchQuery)
    if (filters.name) params.set('name', filters.name)
    if (filters.email) params.set('email', filters.email)
    if (filters.mobile) params.set('mobile', filters.mobile)
    if (filters.country) params.set('country', filters.country)
    if (sortBy !== '-created_at') params.set('ordering', sortBy)
    if (currentPage > 1) params.set('page', currentPage)
    if (itemsPerPage !== 20) params.set('page_size', itemsPerPage)
    setSearchParams(params)
  }, [searchQuery, filters, sortBy, currentPage, itemsPerPage, setSearchParams])

  async function fetchClients(isInitialLoad = false) {
    try {
      if (isInitialLoad) setPageLoading(true); else setClientsLoading(true)
      const params = { page: currentPage, page_size: itemsPerPage }
      if (searchQuery) params.search = searchQuery
      if (filters.name)    params.name = filters.name
      if (filters.email)   params.email = filters.email
      if (filters.mobile)  params.mobile = filters.mobile
      if (filters.country) params.country = filters.country
      if (sortBy) params.ordering = sortBy
      const response = await apiService.get('/clients/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        const arr = data.data || data.results || []
        const pagination = data.pagination || {}
        setClients(arr)
        setTotalCount(pagination.count || arr.length)
        setTotalPages(pagination.total_pages || Math.ceil((pagination.count || arr.length) / itemsPerPage))
      } else {
        showError(response.error?.message || 'Failed to load clients')
      }
    } catch {
      showError('Failed to load clients')
    } finally {
      if (isInitialLoad) setPageLoading(false); else setClientsLoading(false)
    }
  }

  async function handleDeleteClient(clientId) {
    try {
      setConfirmDialog({ isOpen: false })
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/clients/${clientId}/`, { params })
      if (response.success) {
        setClients(prev => prev.filter(c => c.id !== clientId))
        setSelectedClients(prev => prev.filter(id => id !== clientId))
        showSuccess(`${getEntity('client').singular} deleted successfully`)
      } else {
        showError(response.error?.message || 'Failed to delete client')
      }
    } catch {
      showError('Failed to delete client')
    }
  }

  const confirmDeleteClient = (client) => {
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${getEntity('client').singular}`,
      message: `Are you sure you want to delete "${client.name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: `Delete ${getEntity('client').singular}`,
      onConfirm: () => handleDeleteClient(client.id),
    })
  }

  const handleBulkDelete = () => {
    if (selectedClients.length === 0) return
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${selectedClients.length} ${getEntity('client').plural}`,
      message: `Are you sure you want to delete ${selectedClients.length} client(s)? This cannot be undone.`,
      type: 'danger',
      confirmText: `Delete ${selectedClients.length}`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false })
        try {
          await Promise.all(selectedClients.map(clientId => {
            const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
            return apiService.delete(`/clients/${clientId}/`, { params })
          }))
          setClients(prev => prev.filter(c => !selectedClients.includes(c.id)))
          setSelectedClients([])
          showSuccess(`${selectedClients.length} clients deleted`)
        } catch {
          showError('Failed to delete some clients')
        }
      },
    })
  }

  const handleExportClients = async () => {
    try {
      const params = { format: 'csv' }
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get('/clients/export/', { params, responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link); link.click(); document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSuccess(`${getEntity('client').plural} exported successfully`)
    } catch {
      showError('Failed to export clients')
    }
  }

  const toggleSelect = (id) => setSelectedClients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleSelectAll = () => setSelectedClients(selectedClients.length === clients.length ? [] : clients.map(c => c.id))

  const clearFilters = () => {
    setFilters({ name: '', email: '', mobile: '', country: '' })
    setSearchInput(''); setSearchQuery(''); setSortBy('-created_at'); setCurrentPage(1)
  }

  const hasActiveFilters = !!(searchQuery || filters.name || filters.email || filters.mobile || filters.country)
  const activeFilterCount = (searchQuery ? 1 : 0) + Object.values(filters).filter(Boolean).length

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

  if (pageLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineUserGroup}
        iconTone="success"
        title={t('clients.title', { entity: getEntity('client').plural })}
        subtitle={isWorkspaceSwitched
          ? t('clients.subtitleSwitched', { entity: getEntity('client').plural.toLowerCase(), name: user?.switchedAccountName })
          : t('clients.subtitle', { entity: getEntity('client').plural.toLowerCase() })}
        actions={
          <>
            <PermissionGuard resource="client" action="create">
              <Button variant="secondary" leadingIcon={HiOutlineArrowUpTray} onClick={() => setShowImportModal(true)}>
                {t('common.import')}
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="client" action="read">
              <Button variant="secondary" leadingIcon={HiOutlineArrowDownTray} onClick={handleExportClients}>
                {t('common.export')}
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="client" action="create">
              <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/clients/create')}>
                {t('clients.addClient', { entity: getEntity('client').singular })}
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <Card padding="none">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 sm:p-5 border-b border-border-subtle">
          <div className="flex-1 max-w-2xl">
            <Input
              leadingIcon={HiOutlineMagnifyingGlass}
              placeholder={t('clients.searchPlaceholder', { entity: getEntity('client').plural.toLowerCase() })}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              trailingAction={
                isSearching ? <Spinner size="sm" className="me-2" />
                : searchInput ? (
                  <IconButton
                    icon={HiOutlineXMark}
                    aria-label={t('common.clearSearch')}
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSearchInput(''); setSearchQuery('') }}
                  />
                ) : null
              }
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1) }}
              className="min-w-[10rem]"
            >
              <option value="-created_at">{t('sort.newest')}</option>
              <option value="created_at">{t('sort.oldest')}</option>
              <option value="name">{t('sort.nameAsc')}</option>
              <option value="-name">{t('sort.nameDesc')}</option>
            </Select>

            <Button
              variant={showFilters || hasActiveFilters ? 'secondary' : 'ghost'}
              leadingIcon={HiOutlineFunnel}
              onClick={() => setShowFilters(v => !v)}
            >
              {t('common.filters')}
              {hasActiveFilters && <Badge tone="brand" size="sm" className="ms-1">{activeFilterCount}</Badge>}
            </Button>
          </div>

          {selectedClients.length > 0 && (
            <div className="flex items-center gap-2 ps-3 border-s border-border-subtle">
              <Badge tone="brand">{t('common.selected', { count: selectedClients.length })}</Badge>
              <PermissionGuard resource="client" action="delete">
                <Button variant="danger" size="sm" leadingIcon={HiOutlineTrash} onClick={handleBulkDelete}>
                  {t('common.delete')}
                </Button>
              </PermissionGuard>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="p-4 sm:p-5 bg-muted/40 border-b border-border-subtle">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <FormField label={t('clients.filterName')}>
                <Input
                  value={filters.name}
                  onChange={(e) => { setFilters(f => ({ ...f, name: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('clients.filterName')}
                />
              </FormField>
              <FormField label={t('clients.filterEmail')}>
                <Input
                  value={filters.email}
                  onChange={(e) => { setFilters(f => ({ ...f, email: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('clients.filterEmail')}
                />
              </FormField>
              <FormField label={t('clients.filterMobile')}>
                <Input
                  value={filters.mobile}
                  onChange={(e) => { setFilters(f => ({ ...f, mobile: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('clients.filterMobile')}
                />
              </FormField>
              <FormField label={t('clients.filterCountry')}>
                <Input
                  value={filters.country}
                  onChange={(e) => { setFilters(f => ({ ...f, country: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('clients.filterCountry')}
                />
              </FormField>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-ink-muted">{t('filters.results', { count: totalCount })}</span>
                <Button variant="link" size="sm" onClick={clearFilters}>{t('common.clearAllFilters')}</Button>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          {clientsLoading && (
            <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-xl">
              <div className="inline-flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm text-ink-secondary">{t('common.loading')}</span>
              </div>
            </div>
          )}

          {clients.length === 0 ? (
            <EmptyState
              icon={HiOutlineUserGroup}
              title={t('clients.noFound', { entity: getEntity('client').plural.toLowerCase() })}
              description={
                searchQuery
                  ? t('clients.noMatchSearch', { entity: getEntity('client').plural.toLowerCase(), query: searchQuery })
                  : canCreate('client')
                    ? t('clients.getStarted', { entity: getEntity('client').singular.toLowerCase() })
                    : t('clients.noAvailable', { entity: getEntity('client').plural.toLowerCase() })
              }
              action={
                !searchQuery && (
                  <PermissionGuard resource="client" action="create">
                    <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/clients/create')}>
                      {t('clients.addFirst', { entity: getEntity('client').singular.toLowerCase() })}
                    </Button>
                  </PermissionGuard>
                )
              }
            />
          ) : (
            <>
              <div className="px-4 sm:px-5 py-2.5 border-b border-border-subtle bg-muted/40">
                <Checkbox
                  checked={selectedClients.length === clients.length && clients.length > 0}
                  onChange={toggleSelectAll}
                  disabled={clientsLoading}
                >
                  {t('cases.selectAll', {
                    count: clients.length,
                    entity: clients.length !== 1 ? getEntity('client').plural.toLowerCase() : getEntity('client').singular.toLowerCase(),
                  })}
                </Checkbox>
              </div>

              <div className={`p-4 sm:p-5 transition-opacity ${clientsLoading ? 'opacity-50' : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {clients.map((client) => (
                    <ClientCard
                      key={client.id}
                      client={client}
                      selected={selectedClients.includes(client.id)}
                      onToggle={() => toggleSelect(client.id)}
                      onView={() => navigate(`/clients/${client.id}`)}
                      onEdit={() => navigate(`/clients/${client.id}/edit`)}
                      onDelete={() => confirmDeleteClient(client)}
                      formatDate={formatDate}
                      getEntity={getEntity}
                      disabled={clientsLoading}
                    />
                  ))}
                </div>
              </div>

              <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 sm:p-5 border-t border-border-subtle bg-muted/30 transition-opacity ${clientsLoading ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-ink-muted">
                    {t('pagination.showing')}{' '}
                    <span className="font-semibold text-ink-primary">
                      {clients.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}
                    </span>{' '}
                    –{' '}
                    <span className="font-semibold text-ink-primary">
                      {Math.min(currentPage * itemsPerPage, totalCount)}
                    </span>{' '}
                    {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalCount}</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-ink-muted">{t('pagination.show')}:</span>
                    <Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1) }}
                      disabled={clientsLoading}
                      className="w-auto"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={clientsLoading}
                    onChange={setCurrentPage}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      <ClientImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={() => { fetchClients(); setShowImportModal(false) }}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}

function ClientCard({ client, selected, onToggle, onView, onEdit, onDelete, formatDate, getEntity, disabled }) {
  const { t } = useTranslation()
  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-border-strong hover:shadow-token-sm transition-all">
      <div className="flex items-start gap-3 mb-3">
        <div className="pt-1">
          <Checkbox checked={selected} onChange={onToggle} disabled={disabled} aria-label={t('common.selectAll')} />
        </div>
        <Avatar name={client.name} size="lg" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-ink-primary truncate">{client.name}</h3>
          <p className="text-xs text-ink-muted">{t('clients.added', { date: formatDate(client.created_at) })}</p>
        </div>
      </div>

      <dl className="space-y-1.5 text-sm text-ink-secondary mb-3">
        {client.email && (
          <div className="flex items-center gap-2">
            <HiOutlineEnvelope className="w-4 h-4 text-ink-muted" />
            <span className="truncate">{client.email}</span>
          </div>
        )}
        {client.mobile && (
          <div className="flex items-center gap-2">
            <HiOutlinePhone className="w-4 h-4 text-ink-muted" />
            <span>{client.mobile}</span>
          </div>
        )}
        {client.country && (
          <div className="flex items-center gap-2">
            <HiOutlineGlobeAmericas className="w-4 h-4 text-ink-muted" />
            <span>{client.country.name}</span>
          </div>
        )}
      </dl>

      <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
        <Button variant="link" size="sm" onClick={onView} disabled={disabled}>
          {t('common.viewDetails')}
        </Button>
        <div className="flex items-center gap-1">
          <EditButton resource="client">
            <IconButton
              icon={HiOutlinePencil}
              aria-label={t('common.edit')}
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={disabled}
            />
          </EditButton>
          <DeleteButton resource="client">
            <IconButton
              icon={HiOutlineTrash}
              aria-label={t('common.delete')}
              variant="danger"
              size="sm"
              onClick={onDelete}
              disabled={disabled}
            />
          </DeleteButton>
        </div>
      </div>
    </div>
  )
}

function Pagination({ currentPage, totalPages, disabled, onChange }) {
  const { t } = useTranslation()
  const maxVisible = 5
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)
  if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1)
  const pages = []
  for (let i = startPage; i <= endPage; i++) pages.push(i)

  return (
    <div className="flex items-center gap-1">
      <IconButton icon={HiOutlineChevronDoubleLeft} aria-label={t('pagination.firstPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(1)} />
      <IconButton icon={HiOutlineChevronLeft} aria-label={t('pagination.previousPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(Math.max(1, currentPage - 1))} />
      {pages.map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          disabled={disabled}
          className={`min-w-[2rem] h-8 px-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
            i === currentPage ? 'bg-brand text-white' : 'text-ink-secondary hover:bg-muted'
          }`}
        >
          {i}
        </button>
      ))}
      {currentPage < totalPages - 2 && (
        <>
          <span className="px-1 text-ink-muted text-sm">…</span>
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            disabled={disabled}
            className="min-w-[2rem] h-8 px-2 text-sm font-medium text-ink-secondary hover:bg-muted rounded-md disabled:opacity-50"
          >
            {totalPages}
          </button>
        </>
      )}
      <IconButton icon={HiOutlineChevronRight} aria-label={t('pagination.nextPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(Math.min(totalPages, currentPage + 1))} />
      <IconButton icon={HiOutlineChevronDoubleRight} aria-label={t('pagination.lastPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(totalPages)} />
    </div>
  )
}
